/**
 * Icon Utility - Main icon package using lucide-react (react-icons/lu)
 * 
 * This file provides a centralized way to import icons from react-icons.
 * Primary icon set: lucide-react (lu)
 * Can be complemented by other icon sets from react-icons as needed.
 * 
 * Usage:
 *   import { Home, Folder, Users, FileText } from '../utils/icons';
 *   // Or use the Lu-prefixed names: LuHouse, LuFolder, LuUsers, etc.
 */

// Main icon set - Lucide React Icons (lu) - Import with Lu prefix
import {
  // Navigation
  LuHouse,
  LuMenu,
  LuX,
  LuChevronRight,
  LuChevronLeft,
  LuChevronDown,
  LuChevronUp,
  LuArrowLeft,
  LuArrowRight,
  
  // Files & Folders
  LuFolder,
  LuFolderOpen,
  LuFolderPlus,
  LuFileText,
  LuFileCheck,
  LuFileX,
  LuUpload,
  LuDownload,
  
  // Users & People
  LuUsers,
  LuUser,
  LuUserPlus,
  LuUserCheck,
  LuUserX,
  
  // Actions
  LuPlus,
  LuMinus,
  LuPen,
  LuTrash,
  LuSave,
  LuSearch,
  LuFilter,
  LuSettings,
  LuEllipsisVertical,
  LuEllipsis,
  
  // Status & Feedback
  LuCheck,
  LuCircleCheck,
  LuSquareCheck,
  LuCircleX,
  LuCircleAlert,
  LuInfo,
  LuTriangleAlert,
  LuClock,
  LuCalendar,
  
  // Business
  LuCreditCard,
  LuDollarSign,
  LuTrendingUp,
  LuTrendingDown,
  LuChartBar,
  LuChartPie,
  LuChartLine,
  LuActivity,
  
  // Communication
  LuMail,
  LuMessageSquare,
  LuBell,
  LuBellOff,
  LuSend,
  
  // Media
  LuImage,
  LuVideo,
  LuMusic,
  
  // System
  LuLock,
  LuLockOpen,
  LuEye,
  LuEyeOff,
  LuSun,
  LuMoon,
  LuLogOut,
  LuLogIn,
  
  // Layout
  LuLayoutGrid,
  LuList,
  LuLayoutTemplate,
  LuPanelLeft,
  LuPanelLeftOpen,
  LuPanelLeftClose,

  // Places & Locations
  LuGlobe,
  LuMapPin,
  LuBuilding2,
  LuLandmark,
  LuScale,

  // Other common icons
  LuStar,
  LuHeart,
  LuBookmark,
  LuShare,
  LuCopy,
  LuLink,
  LuExternalLink,
  LuRefreshCw,
  LuLoaderCircle,
  
  // Additional icons for public website
  LuTruck,
  LuShoppingBag,
  LuHeadphones,
} from 'react-icons/lu';

// Re-export with cleaner names (aliases)
export {
  // Navigation
  LuHouse as Home,
  LuMenu as Menu,
  LuX as X,
  LuChevronRight as ChevronRight,
  LuChevronLeft as ChevronLeft,
  LuChevronDown as ChevronDown,
  LuChevronUp as ChevronUp,
  LuArrowLeft as ArrowLeft,
  LuArrowRight as ArrowRight,
  
  // Files & Folders
  LuFolder as Folder,
  LuFolderOpen as FolderOpen,
  LuFolderPlus as FolderPlus,
  LuFileText as FileText,
  LuFileText as File,
  LuFileCheck as FileCheck,
  LuFileX as FileX,
  LuUpload as Upload,
  LuDownload as Download,
  
  // Users & People
  LuUsers as Users,
  LuUser as User,
  LuUserPlus as UserPlus,
  LuUserCheck as UserCheck,
  LuUserX as UserX,
  
  // Actions
  LuPlus as Plus,
  LuMinus as Minus,
  LuPen as Edit,
  LuTrash as Trash,
  LuSave as Save,
  LuSearch as Search,
  LuFilter as Filter,
  LuSettings as Settings,
  LuEllipsisVertical as MoreVertical,
  LuEllipsis as MoreHorizontal,
  
  // Status & Feedback
  LuCheck as Check,
  LuCircleCheck as CheckCircle,
  LuSquareCheck as CheckSquare,
  LuCircleX as XCircle,
  LuCircleAlert as AlertCircle,
  LuInfo as Info,
  LuTriangleAlert as Warning,
  LuClock as Clock,
  LuCalendar as Calendar,
  
  // Business
  LuCreditCard as CreditCard,
  LuDollarSign as DollarSign,
  LuTrendingUp as TrendingUp,
  LuTrendingDown as TrendingDown,
  LuChartBar as BarChart,
  LuChartPie as PieChart,
  LuChartLine as LineChart,
  LuActivity as Activity,
  
  // Communication
  LuMail as Mail,
  LuMessageSquare as MessageSquare,
  LuBell as Bell,
  LuBellOff as BellOff,
  LuSend as Send,
  
  // Media
  LuImage as Image,
  LuVideo as Video,
  LuMusic as Music,
  
  // System
  LuLock as Lock,
  LuLockOpen as Unlock,
  LuEye as Eye,
  LuEyeOff as EyeOff,
  LuSun as Sun,
  LuMoon as Moon,
  LuLogOut as LogOut,
  LuLogIn as LogIn,
  
  // Layout
  LuLayoutGrid as Grid,
  LuList as List,
  LuLayoutTemplate as Layout,
  LuPanelLeft as Sidebar,
  LuPanelLeftOpen as SidebarOpen,
  LuPanelLeftClose as SidebarClose,

  // Places & Locations
  LuGlobe as Globe,
  LuMapPin as MapPin,
  LuBuilding2 as Building2,
  LuLandmark as Landmark,
  LuScale as Scale,

  // Other common icons
  LuStar as Star,
  LuHeart as Heart,
  LuBookmark as Bookmark,
  LuShare as Share,
  LuCopy as Copy,
  LuLink as Link,
  LuExternalLink as ExternalLink,
  LuRefreshCw as RefreshCw,
  LuLoaderCircle as Loader,
  
  // Additional icons for public website
  LuTruck as Truck,
  LuShoppingBag as ShoppingBag,
  LuHeadphones as Headphones,
};

// Also export the original Lu-prefixed names for direct use
export {
  LuHouse,
  LuMenu,
  LuX,
  LuChevronRight,
  LuChevronLeft,
  LuChevronDown,
  LuChevronUp,
  LuArrowLeft,
  LuArrowRight,
  LuFolder,
  LuFolderOpen,
  LuFolderPlus,
  LuFileText,
  LuFileCheck,
  LuFileX,
  LuUpload,
  LuDownload,
  LuUsers,
  LuUser,
  LuUserPlus,
  LuUserCheck,
  LuUserX,
  LuPlus,
  LuMinus,
  LuPen,
  LuTrash,
  LuSave,
  LuSearch,
  LuFilter,
  LuSettings,
  LuEllipsisVertical,
  LuEllipsis,
  LuCheck,
  LuCircleCheck,
  LuSquareCheck,
  LuCircleX,
  LuCircleAlert,
  LuInfo,
  LuTriangleAlert,
  LuClock,
  LuCalendar,
  LuCreditCard,
  LuDollarSign,
  LuTrendingUp,
  LuTrendingDown,
  LuChartBar,
  LuChartPie,
  LuChartLine,
  LuActivity,
  LuMail,
  LuMessageSquare,
  LuBell,
  LuBellOff,
  LuSend,
  LuImage,
  LuVideo,
  LuMusic,
  LuLock,
  LuLockOpen,
  LuEye,
  LuEyeOff,
  LuSun,
  LuMoon,
  LuLogOut,
  LuLogIn,
  LuLayoutGrid,
  LuList,
  LuLayoutTemplate,
  LuPanelLeft,
  LuPanelLeftOpen,
  LuPanelLeftClose,
  LuGlobe,
  LuMapPin,
  LuBuilding2,
  LuLandmark,
  LuScale,
  LuStar,
  LuHeart,
  LuBookmark,
  LuShare,
  LuCopy,
  LuLink,
  LuExternalLink,
  LuRefreshCw,
  LuLoaderCircle,
  LuTruck,
  LuShoppingBag,
  LuHeadphones,
};

// Additional icon sets can be imported here as needed
// Example: Font Awesome
export {
  FaHome,
  FaFolder,
  FaUsers,
  FaFileAlt,
  FaCheckCircle,
  FaCalendar,
  FaCreditCard,
  FaChartBar,
} from 'react-icons/fa';

// Example: Material Design Icons
export {
  MdDashboard,
  MdSettings,
  MdNotifications,
  MdAccountCircle,
} from 'react-icons/md';

// Example: Feather Icons
export {
  FiHome,
  FiFolder,
  FiUsers,
} from 'react-icons/fi';

// Example: Heroicons
export {
  HiHome,
  HiFolder,
  HiUsers,
} from 'react-icons/hi';
